import type { PortableTextBlock } from '@portabletext/types';

import { toSlug } from '@lib/page-utils/player-profile/slugs';

export type ArticleHeading = { id: string; text: string };

const SECTION_HEADING_STYLE = 'h2';
const WORDS_PER_MINUTE = 230;

const blockText = (block: PortableTextBlock): string =>
  (block.children ?? [])
    .map((child) => ('text' in child ? String(child.text) : ''))
    .join('')
    .trim();

/**
 * The table of contents and the rail are generated from the body's Heading 2s,
 * so editors never maintain a list by hand. Ids are de-duplicated so two
 * identical headings still get distinct anchors.
 */
export const extractHeadings = (
  body: PortableTextBlock[] | undefined
): ArticleHeading[] => {
  const seen = new Map<string, number>();
  const headings: ArticleHeading[] = [];
  for (const block of body ?? []) {
    if (block._type !== 'block' || block.style !== SECTION_HEADING_STYLE)
      continue;
    const text = blockText(block);
    if (!text) continue;
    const base = toSlug(text) || 'section';
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    headings.push({ id: count === 0 ? base : `${base}-${count + 1}`, text });
  }
  return headings;
};

export const headingIdFor = (
  headings: ArticleHeading[],
  text: string
): string | undefined => {
  const index = headings.findIndex((heading) => heading.text === text);
  if (index === -1) return undefined;
  // Consume the match so a repeated heading resolves to its own id.
  const [heading] = headings.splice(index, 1);
  return heading.id;
};

export const countBodyWords = (body: PortableTextBlock[] | undefined): number =>
  (body ?? [])
    .filter((block) => block._type === 'block')
    .map(blockText)
    .join(' ')
    .split(/\s+/)
    .filter(Boolean).length;

export const estimateReadMinutes = (
  body: PortableTextBlock[] | undefined
): number => Math.max(1, Math.round(countBodyWords(body) / WORDS_PER_MINUTE));
